/**
 * Shadow DOM Utilities for Widget Isolation
 *
 * Provides complete CSS and DOM isolation for the widget.
 * All styles are scoped to the shadow root, preventing conflicts
 * with client site styles.
 */

export interface ShadowDOMConfig {
  mode?: "open" | "closed"; // "open" allows external access, "closed" is more secure
  delegatesFocus?: boolean; // Allow focus delegation for accessibility
  // When provided, host attaches inside this element with `position: absolute`
  // so the widget is bounded to the target. Defaults to `document.body` with
  // `position: fixed` covering the viewport.
  parent?: HTMLElement;
}

export interface ShadowContainer {
  host: HTMLElement;
  shadowRoot: ShadowRoot;
  reactContainer: HTMLElement;
}

/**
 * Creates a Shadow DOM container for the widget
 * Returns the host element, shadow root, and React container
 */
export function createShadowContainer(
  containerId: string,
  zIndex: number = 9999,
  config: ShadowDOMConfig = {}
): ShadowContainer {
  const { mode = "open", delegatesFocus = true, parent } = config;
  const hostParent = parent ?? document.body;
  const positionStyle = parent ? "absolute" : "fixed";

  // Check if container already exists
  let host = document.getElementById(containerId) as HTMLElement;

  if (host && host.shadowRoot) {
    // Reuse existing shadow root
    const shadowRoot = host.shadowRoot;
    let reactContainer = shadowRoot.getElementById(`${containerId}-react-root`);

    if (!reactContainer) {
      reactContainer = document.createElement("div");
      reactContainer.id = `${containerId}-react-root`;
      reactContainer.style.cssText = `
        width: 100%;
        height: 100%;
        pointer-events: none;
      `;
      shadowRoot.appendChild(reactContainer);
    }

    return { host, shadowRoot, reactContainer };
  }

  // Create new host element
  host = document.createElement("div");
  host.id = containerId;
  host.setAttribute("data-widget", "unibo-widget");

  // Host must establish a positioned containing block so absolute children
  // (the open overlay) are bounded to it.
  host.style.cssText = `
    position: ${positionStyle};
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: ${zIndex};
    isolation: isolate;
  `;

  // Create Shadow Root
  const shadowRoot = host.attachShadow({
    mode,
    delegatesFocus,
  });

  // Create inner container for React (inside shadow DOM)
  const reactContainer = document.createElement("div");
  reactContainer.id = `${containerId}-react-root`;
  reactContainer.style.cssText = `
    width: 100%;
    height: 100%;
    pointer-events: none;
  `;
  shadowRoot.appendChild(reactContainer);

  hostParent.appendChild(host);

  return { host, shadowRoot, reactContainer };
}

/**
 * Injects CSS styles into Shadow DOM using Constructable Stylesheets (modern)
 * Falls back to style element for older browsers
 */
export async function injectStylesIntoShadow(
  shadowRoot: ShadowRoot,
  cssText: string
): Promise<void> {
  // Check if Constructable Stylesheets are supported
  if ("adoptedStyleSheets" in shadowRoot) {
    try {
      const sheet = new CSSStyleSheet();
      await sheet.replace(cssText);
      shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
      return;
    } catch (error) {
      console.warn(
        "[Shadow DOM] Constructable Stylesheet failed, using fallback:",
        error
      );
      // Fall through to style element fallback
    }
  }

  // Fallback: Use style element (works everywhere)
  const styleElement = document.createElement("style");
  styleElement.textContent = cssText;
  shadowRoot.insertBefore(styleElement, shadowRoot.firstChild);
}

/**
 * Injects external stylesheet link into Shadow DOM
 * Useful for fonts or external CSS files
 */
export function injectStylesheetIntoShadow(
  shadowRoot: ShadowRoot,
  href: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if link already exists
    const existingLink = Array.from(shadowRoot.querySelectorAll("link")).find(
      (link) => link.href === href
    );

    if (existingLink) {
      resolve();
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;

    link.onload = () => resolve();
    link.onerror = () =>
      reject(new Error(`Failed to load stylesheet: ${href}`));

    shadowRoot.insertBefore(link, shadowRoot.firstChild);
  });
}

/**
 * Checks if Shadow DOM is supported in the current browser
 */
export function isShadowDOMSupported(): boolean {
  return (
    typeof HTMLElement !== "undefined" &&
    "attachShadow" in HTMLElement.prototype
  );
}
